import { useState, useEffect } from "react";
import { supabase } from "../../../../../../integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { Skeleton } from "../../../../../ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, subDays } from "date-fns";

interface VolumeChartProps {
  dateRange: string;
  platform: string;
  outletIds?: string[];
  supabaseoutletIDs: string[];
}

export const VolumeChart = ({ dateRange, platform, outletIds, supabaseoutletIDs }: VolumeChartProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [dateRange, platform, outletIds]);

  const loadData = async () => {
    setLoading(true);
    try {
      const daysAgo = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      let query = supabase
        .from("reviews")
        .select("posted_at")
        .gte("posted_at", startDate.toISOString());

      if (platform !== "all") {
        query = query.eq("platform", platform as any);
      }

      const isAllSelected = !outletIds || outletIds.length === 0 || outletIds.includes("all");
      if (!isAllSelected && outletIds.length > 0) {
        query = query.in("outlet_id", outletIds);
      }else {
        query = query.in("outlet_id", supabaseoutletIDs);        
      }

      const { data: reviews, error } = await query;
      if (error) throw error;

      const dailyCounts: Record<string, number> = {};
      for (let i = 0; i < daysAgo; i++) {
        const date = format(subDays(new Date(), i), "MMM dd");
        dailyCounts[date] = 0;
      }

      reviews?.forEach((r) => {
        const date = format(new Date(r.posted_at), "MMM dd");
        if (dailyCounts[date] !== undefined) {
          dailyCounts[date] += 1;
        }
      });

      const chartData = Object.entries(dailyCounts)
        .map(([date, count]) => ({ date, count }))
        .reverse();

      setData(chartData);
    } catch (error) {
      console.error("Error loading volume data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review Volume Over Time</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
